import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import {
  Box,
  TableContainer,
  Table as MUITable,
  TableHead as MUITableHead,
  TableBody as MUITableBody,
  TableRow as MUITableRow,
  TableCell as MUITableCell,
  CircularProgress,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { IPaginationProps } from '@occc/ui-design-system';
import { Pagination, EmptyDataTable } from '@occc/ui-design-system';
import { DateTimeFormat, formatDateTime, parseDate } from '@occc-app/lib/date';

import type { ConversationListItem } from './chat-history.types';

import { routeCreator } from '../../drive-insights.router';

type ChatHistoryTableProps = {
  conversations: ConversationListItem[] | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  paginationProps: IPaginationProps;
};

const HEADERS = [{ label: 'Name' }, { label: 'Created' }] as const;

const formatTableDate = (date: string) => {
  if (!date) {
    return 'Invalid date';
  }

  try {
    return formatDateTime(parseDate(date), DateTimeFormat.DateTime12h);
  } catch {
    return 'Invalid date';
  }
};

type RenderTableProps = {
  items: ConversationListItem[];
  onRowClick: (conversationId: string) => void;
  paginationProps: IPaginationProps;
  hoverBg: string;
};

const renderTable = ({ items, onRowClick, paginationProps, hoverBg }: RenderTableProps) => {
  return (
    <>
      <TableContainer>
        <MUITable>
          <MUITableHead>
            <MUITableRow>
              {HEADERS.map((header, index) => {
                const { label } = header;
                return (
                  <MUITableCell
                    key={label || index}
                    sx={{
                      whiteSpace: 'nowrap',
                      backgroundColor: 'white',
                      color: 'drive.textPrimary',
                      fontWeight: 600,
                      borderBottom: 1,
                      borderBottomColor: 'drive.border',
                    }}
                  >
                    {label}
                  </MUITableCell>
                );
              })}
            </MUITableRow>
          </MUITableHead>
          <MUITableBody>
            {items.map((conversation) => (
              <MUITableRow
                key={conversation.id}
                onClick={() => onRowClick(conversation.id)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: hoverBg },
                  '& .MuiTableCell-root': {
                    color: 'drive.textPrimary',
                    borderBottom: 1,
                    borderBottomColor: 'drive.border',
                  },
                }}
              >
                <MUITableCell>{conversation.name}</MUITableCell>
                <MUITableCell>{formatTableDate(conversation.createdAt)}</MUITableCell>
              </MUITableRow>
            ))}
          </MUITableBody>
        </MUITable>
      </TableContainer>
      <Box sx={{ borderTop: 1, borderTopColor: 'drive.border' }}>
        <Pagination {...paginationProps} />
      </Box>
    </>
  );
};

export const ChatHistoryTable = ({
  conversations,
  loading,
  error,
  paginationProps,
}: ChatHistoryTableProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const hoverBg = alpha(theme.palette.primary.main, 0.04);

  const handleRowClick = useCallback(
    (conversationId: string) => {
      navigate(`${routeCreator.base()}?conversationId=${conversationId}`);
    },
    [navigate],
  );

  if (error) {
    return (
      <Box p={4}>
        <Typography color='error' data-testid='something-went-wrong-msg'>
          Something went wrong. Please try again.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={4} display='flex' justifyContent='center' alignItems='center'>
        <CircularProgress />
      </Box>
    );
  }

  if (!conversations) {
    return (
      <Box p={4}>
        <Typography color='error' data-testid='no-data-msg'>
          No data. Something went wrong. Please try again.
        </Typography>
      </Box>
    );
  }

  if (conversations.length === 0) {
    return <EmptyDataTable />;
  }

  return renderTable({
    items: conversations,
    onRowClick: handleRowClick,
    paginationProps,
    hoverBg,
  });
};
